import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, Check, MapPin } from 'lucide-react';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';

/**
 * Alocar um volume: dizer em que posição a etiqueta foi guardada.
 *
 * Um só diálogo para os três caminhos que levam até aqui — a gaveta de
 * pendências, a lista "a guardar" do romaneio e a aba própria. São entradas
 * diferentes para o MESMO gesto, e ter três telas parecidas seria três lugares
 * para a regra divergir.
 *
 * O CAMPO SÓ OFERECE POSIÇÃO DO CENTRO
 * ------------------------------------
 * A busca de localizador é filtrada pelo centro no servidor. Não é conveniência:
 * nenhuma procedure do ERP confere se o destino é da mesma empresa
 * (`Usp_ValidarLocalizadorAlocacao` só checa se o número existe), então oferecer
 * uma posição de outra empresa seria oferecer um erro que só apareceria no
 * inventário. A gravação recusa de novo — mas o campo não deve nem sugerir.
 *
 * CONFIRMAR É UM PASSO SEPARADO
 * -----------------------------
 * Alocar MOVE ESTOQUE: dá saída no centro de recebimento e entrada no
 * localizador. Não tem desfazer nesta tela.
 */

export interface EtiquetaParaAlocar {
  codigoBarras: string;
  recebimento: number;
  produto: number;
  descricao: string | null;
  quantidade: number | null;
  sequencia: number | null;
}

interface LocalizadorDestino {
  localizador: number;
  endereco: string | null;
  filial: number | null;
}

export function DialogoAlocacao({
  etiqueta,
  empresa,
  centro,
  aoFechar,
  aoConcluir,
}: {
  etiqueta: EtiquetaParaAlocar | null;
  empresa: number | null;
  centro: number | null;
  aoFechar: () => void;
  aoConcluir?: (() => void) | undefined;
}): JSX.Element {
  const cliente = useQueryClient();
  const [busca, definirBusca] = React.useState('');
  const [escolhido, definirEscolhido] = React.useState<LocalizadorDestino | null>(null);
  const [erro, definirErro] = React.useState<string | null>(null);
  const buscaAtrasada = useDebounce(busca, 300);

  React.useEffect(() => {
    definirBusca('');
    definirEscolhido(null);
    definirErro(null);
  }, [etiqueta?.codigoBarras]);

  const sugestoes = useQuery<{ localizadores: LocalizadorDestino[] }>({
    queryKey: ['recebimento-localizadores', centro, buscaAtrasada],
    queryFn: () =>
      api.get<{ localizadores: LocalizadorDestino[] }>('/api/recebimentos/localizadores', {
        centro,
        q: buscaAtrasada.trim(),
      }),
    enabled: centro !== null && buscaAtrasada.trim().length >= 2,
    staleTime: 60_000,
  });

  const alocar = useMutation({
    mutationFn: (destino: number) =>
      api.post<{ paleteAlocacao: number; endereco: string | null }>('/api/recebimentos/alocar', {
        codigoBarras: etiqueta?.codigoBarras,
        localizador: destino,
        centro,
        empresa,
      }),
    onSuccess: async () => {
      definirErro(null);
      await cliente.invalidateQueries({ queryKey: ['etiquetas-alocacao'] });
      await cliente.invalidateQueries({ queryKey: ['recebimento-pendencias'] });
      aoConcluir?.();
      aoFechar();
    },
    onError: (e) => definirErro(e instanceof Error ? e.message : 'Não foi possível alocar.'),
  });

  const lista = sugestoes.data?.localizadores ?? [];

  return (
    <Dialog.Root open={etiqueta !== null} onOpenChange={(v) => !v && aoFechar()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 flex max-h-[min(90vh,680px)] w-[min(540px,94vw)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-xl border border-borda bg-superficie p-4 shadow-2xl">
          <Dialog.Title className="shrink-0 text-base font-semibold">Alocar volume</Dialog.Title>
          <Dialog.Description className="mt-1 shrink-0 text-[12.5px] text-textoFraco">
            Informe a posição em que este volume foi guardado. A alocação move o estoque.
          </Dialog.Description>

          {etiqueta ? (
            <div className="mt-3 shrink-0 rounded-xl border border-borda bg-superficie2 px-3 py-2.5">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                <span className="tabular font-mono text-[15px] font-bold">
                  {etiqueta.codigoBarras}
                </span>
                <span className="text-[12px] text-textoFraco">
                  recebimento <strong className="text-texto">{etiqueta.recebimento}</strong>
                </span>
                {etiqueta.sequencia !== null ? (
                  <span className="text-[12px] text-textoFraco">seq {etiqueta.sequencia}</span>
                ) : null}
                {etiqueta.quantidade !== null ? (
                  <span className="text-[12px] text-textoFraco">
                    <strong className="text-texto">{numero(etiqueta.quantidade)}</strong> un
                  </span>
                ) : null}
              </div>
              <p className="mt-1 truncate text-[12.5px]" title={etiqueta.descricao ?? ''}>
                <span className="tabular font-mono text-textoFraco">{etiqueta.produto}</span>{' '}
                {etiqueta.descricao ?? '—'}
              </p>
            </div>
          ) : null}

          <div className="mt-3 shrink-0">
            <label htmlFor="busca-localizador" className="rotulo">
              Posição de destino
            </label>
            <input
              id="busca-localizador"
              value={busca}
              onChange={(e) => {
                definirBusca(e.target.value);
                definirEscolhido(null);
              }}
              autoFocus
              autoComplete="off"
              placeholder="código ou endereço — ex.: 68331 ou 22 59 202"
              className="mt-1 min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-3 text-[14px] outline-none focus:border-primaria focus:ring-2 focus:ring-primaria/25"
            />
            <p className="mt-1 text-[11px] text-textoFraco">
              Só posições do centro {centro ?? '—'} — a alocação em outra empresa é recusada.
            </p>
          </div>

          <div className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-lg border border-borda">
            {buscaAtrasada.trim().length < 2 ? (
              <p className="px-3 py-4 text-center text-[12px] text-textoFraco">
                Digite ao menos 2 caracteres para procurar.
              </p>
            ) : sugestoes.isLoading ? (
              <p className="px-3 py-4 text-center text-[12px] text-textoFraco">Procurando…</p>
            ) : lista.length === 0 ? (
              <p className="px-3 py-4 text-center text-[12px] text-textoFraco">
                Nenhuma posição deste centro corresponde.
              </p>
            ) : (
              <ul className="divide-y divide-borda">
                {lista.map((l) => (
                  <li key={l.localizador}>
                    <button
                      type="button"
                      onClick={() => definirEscolhido(l)}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-superficie2',
                        escolhido?.localizador === l.localizador && 'bg-primaria-suave',
                      )}
                    >
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-textoFraco" />
                      <span className="tabular font-mono font-bold">{l.endereco ?? '—'}</span>
                      <span className="tabular ml-auto font-mono text-[11.5px] text-textoFraco">
                        {l.localizador}
                      </span>
                      {escolhido?.localizador === l.localizador ? (
                        <Check className="h-4 w-4 shrink-0 text-primaria" />
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {erro ? (
            <div className="mt-2 flex shrink-0 items-start gap-2 rounded-lg border border-perigo/40 bg-perigo/10 px-3 py-2 text-[12.5px]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-perigo" />
              <span>{erro}</span>
            </div>
          ) : null}

          <div className="mt-3 flex shrink-0 items-center justify-end gap-2">
            {escolhido ? (
              <span className="mr-auto flex items-center gap-1.5 text-[12.5px]">
                <span className="tabular font-mono text-textoFraco">
                  {etiqueta?.codigoBarras}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-textoFraco" />
                <span className="tabular font-mono font-bold">{escolhido.endereco}</span>
              </span>
            ) : null}
            <Botao variante="secundaria" tamanho="pequeno" onClick={aoFechar}>
              Cancelar
            </Botao>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              disabled={escolhido === null}
              carregando={alocar.isPending}
              onClick={() => escolhido && alocar.mutate(escolhido.localizador)}
            >
              <Check className="h-4 w-4" />
              Alocar
            </Botao>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
